/**
 * TagModel — 标签/多选参照模型
 *
 * ## 架构角色
 * 标签控件与多选参照控件对应的 ViewModel，继承 BaseModel 所有 API。
 * 持有一组标签数据（showValue），支持单选/多选、参照携带、过滤条件等。
 *
 * ## 模型配置（new ttx.models.TagModel(options)）
 * - value: any（默认 undefined）
 * - multiple: Boolean（默认 false）
 * - bill2ReferKeyFieldMap: Object（默认 {}）
 * - refer2BillKeyFieldMap: Object（默认 {}）
 * - textField: String（默认 'name'）
 * - convert: Boolean（默认 true）
 * - needRefCode: Boolean（默认 false）
 * - innerStatusField: String（默认 '_status'）
 * - selfCollected: Boolean（默认 false，true 不获取 false 获取）
 *
 * ## 事件（4 个，通过 on/execute 触发）
 * - beforeBrowse / afterBrowse：参照弹窗前后（外部触发）
 * - beforeValueChange / afterValueChange：值改变前后（setValue 中触发）
 *
 * ## 使用示例
 * ```js
 * var tag = new ttx.models.TagModel({ cItemName: 'tags', multiple: true });
 * tag.setValue([{ name: '标签1' }, { name: '标签2' }]);
 * tag.getShowValueCount(); // 2
 * ```
 */
ttx.models.register('TagModel', function (modelType) {
  const tagModel = function (data) {
    ttx.models.BaseModel.call(this, data);
    // 默认配置
    const opts = data || {};
    this._set_data('value', opts.value !== undefined ? opts.value : undefined, true);
    this._set_data('multiple', opts.multiple === true, true);
    this._set_data('bill2ReferKeyFieldMap', opts.bill2ReferKeyFieldMap || {}, true);
    this._set_data('refer2BillKeyFieldMap', opts.refer2BillKeyFieldMap || {}, true);
    this._set_data('textField', opts.textField || 'name', true);
    this._set_data('convert', opts.convert !== false, true);
    this._set_data('needRefCode', opts.needRefCode === true, true);
    this._set_data('innerStatusField', opts.innerStatusField || '_status', true);
    this._set_data('selfCollected', opts.selfCollected === true, true);
    // showValue 用于显示：多选为数组，单选为值
    const isMultiple = this._get_data('multiple');
    this._set_data('showValue', opts.showValue || (isMultiple ? [] : null), true);
  };
  tagModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
  tagModel.prototype.modelType = modelType;

  // 深拷贝工具
  const _deepCopy = function (data) {
    if (data == null || typeof data !== 'object') return data;
    if (ttx.utils.isArray(data)) return ttx.utils.extend(true, [], data);
    return ttx.utils.extend(true, {}, data);
  };

  // 校验模型选择的值 showValue
  tagModel.prototype.validate = function () {
    const showValue = this.getShowValue();
    const bIsNull = this._get_data('bIsNull');
    // bIsNull=false 表示必输
    if (bIsNull === false && ttx.utils.isEmpty(showValue)) {
      return false;
    }
    return true;
  };

  // 获取模型数据
  tagModel.prototype.getDirtyData = function () {
    const value = this.getValue();
    const showValue = this.getShowValue();
    return { value: _deepCopy(value), showValue: _deepCopy(showValue) };
  };

  // 获取脏数据行 index 数组
  tagModel.prototype.getDirtyRowIndexes = function () {
    const showValue = this.getShowValue();
    if (!ttx.utils.isArray(showValue)) return [];
    const indexes = [];
    const statusField = this._get_data('innerStatusField') || '_status';
    showValue.forEach(function (row, i) {
      if (row && row[statusField] && row[statusField] !== 'Unchanged') indexes.push(i);
    });
    return indexes;
  };

  // 获取模型所有数据，包括已删除
  tagModel.prototype.getAllData = function () {
    return this.getShowValue();
  };

  // 设置模型的数据（value）
  tagModel.prototype.setData = function (data) {
    this.setValue(data, false);
  };

  // 清空模型上的数据，value → []
  tagModel.prototype.clear = function () {
    const oldValue = this.getValue();
    this._set_data('value', [], true);
    this._set_data('showValue', [], true);
    this.execute('afterValueChange', { value: [], oldValue: oldValue });
  };

  // 获取显示的值
  tagModel.prototype.getShowValue = function () {
    const showValue = this._get_data('showValue');
    if (showValue !== undefined && showValue !== null) return showValue;
    return this._get_data('multiple') ? [] : null;
  };

  // 获取显示值的个数
  tagModel.prototype.getShowValueCount = function () {
    const showValue = this.getShowValue();
    if (!showValue) return 0;
    return ttx.utils.isArray(showValue) ? showValue.length : 1;
  };

  // 添加树请求查询条件 treeFilter
  tagModel.prototype.setTreeFilter = function (filter) {
    this._set_data('treeFilter', filter, true);
  };

  // 获取 treeFilter
  tagModel.prototype.getTreeFilter = function () {
    return this._get_data('treeFilter');
  };

  // filter 过滤
  tagModel.prototype.setFilter = function (filter) {
    this._set_data('filter', filter, true);
  };

  // 获取 tagmodel 过滤
  tagModel.prototype.getFilter = function () {
    return this._get_data('filter');
  };

  // 设置 condition
  tagModel.prototype.setCondition = function (condition) {
    this._set_data('condition', condition, true);
  };

  // 获取 condition
  tagModel.prototype.getCondition = function () {
    return this._get_data('condition');
  };

  // 设置模型显示的值 showValue
  // 触发 beforeValueChange（返回 false 中止）/ afterValueChange 事件
  tagModel.prototype.setValue = function (value, check, group) {
    const oldValue = this.getValue();
    const data = { value: value, oldValue: oldValue, check: check, group: group };
    if (!this.execute('beforeValueChange', data)) return;
    this._set_data('value', value, true);
    // 同步 showValue
    let showValue;
    if (ttx.utils.isArray(value)) {
      showValue = value.slice();
    } else if (value && typeof value === 'object') {
      showValue = [value];
    } else {
      showValue = this._get_data('multiple') ? [] : value;
    }
    this._set_data('showValue', showValue, true);
    this.execute('afterValueChange', data);
    // 通知监听器
    this.doPropertyChange('setValue', value);
  };

  // 获取模型上 value 的值
  tagModel.prototype.getValue = function () {
    return this._get_data('value');
  };

  // 删除对应 index 显示的值
  tagModel.prototype.deleteItem = function (index) {
    const showValue = this.getShowValue();
    if (!ttx.utils.isArray(showValue)) return;
    if (index < 0 || index >= showValue.length) return;
    const removed = showValue.splice(index, 1)[0];
    this._set_data('showValue', showValue, true);
    this._set_data('value', showValue, true);
    this.execute('afterValueChange', { value: showValue, removed: removed });
  };

  // 批量删除显示的值
  tagModel.prototype.deleteItems = function (indexs) {
    if (!ttx.utils.isArray(indexs)) return;
    const showValue = this.getShowValue();
    if (!ttx.utils.isArray(showValue)) return;
    // 倒序删除避免索引错乱
    const sorted = indexs.slice().sort(function (a, b) { return b - a; });
    sorted.forEach(function (idx) {
      if (idx >= 0 && idx < showValue.length) showValue.splice(idx, 1);
    });
    this._set_data('showValue', showValue, true);
    this._set_data('value', showValue, true);
    this.execute('afterValueChange', { value: showValue });
  };

  // 设置 value 中某一列的值
  tagModel.prototype.setCellValue = function (index, cellName, cellValue) {
    const showValue = this.getShowValue();
    if (!ttx.utils.isArray(showValue)) return;
    if (index < 0 || index >= showValue.length) return;
    if (!showValue[index] || typeof showValue[index] !== 'object') return;
    showValue[index][cellName] = cellValue;
    this._set_data('showValue', showValue, true);
    this.execute('afterValueChange', { index: index, cellName: cellName, cellValue: cellValue });
  };

  // 批量执行 setCellValue
  // values: [{index, cellName, cellValue}, ...]
  tagModel.prototype.setCellValues = function (values) {
    if (!ttx.utils.isArray(values)) return;
    const self = this;
    values.forEach(function (item) {
      if (!item) return;
      self.setCellValue(item.index, item.cellName, item.cellValue);
    });
  };

  // 设置模型是否多选
  tagModel.prototype.setMultiple = function (multiple) {
    this._set_data('multiple', multiple === true, true);
  };

  // 设置模型 cRefType = code；cRefRetId 是参照携带
  tagModel.prototype.setRefCode = function (code, cRefRetId) {
    this._set_data('cRefType', code, true);
    if (cRefRetId) this._set_data('cRefRetId', cRefRetId, true);
  };

  // 设置焦点行
  tagModel.prototype.setFocusedRowIndex = function (index) {
    this.setCache('focusedRowIndex', index);
    this.execute('focusedRowChange', { index: index });
  };

  // 根据 index 获取行数据
  tagModel.prototype.getRow = function (index) {
    const showValue = this.getShowValue();
    if (!ttx.utils.isArray(showValue)) return null;
    if (index < 0 || index >= showValue.length) return null;
    return _deepCopy(showValue[index]);
  };

  // 获取行 viewmodel
  tagModel.prototype.getEditRowModel = function () {
    // 返回当前焦点行作为编辑模型
    const index = this.getCache('focusedRowIndex') || 0;
    const showValue = this.getShowValue();
    if (!ttx.utils.isArray(showValue)) return null;
    return showValue[index] || null;
  };

  return tagModel;
});
